// Wave 20.43 — Aero Wake bare canvas embed (v3).
//
// Concept (per user sketch): every aero session is a single
// stream-line. All lines originate from a tight bundle on the
// left at Y = avg CdA. Each line then fans out toward its
// position's mean-CdA Y band on the right. Sessions in the same
// position cluster together and share a color. Each line
// animates with per-session turbulence — dialed sessions read
// as smooth arcs, chaotic sessions writhe.
//
// No orb, no silhouette, no attractor. Streams tell the whole
// story: how many sessions, how they cluster by position,
// how consistent each position is.
//
// URL params (all optional; missing → demo dataset):
//   ?avgCda=0.25                                     — origin bundle Y
//   ?positions=aero:0.22:0.02:5,drops:0.28:0.05:8    — class:meanCda:variance:sessionCount
//                                                     variance drives per-session turbulence
//                                                     around meanCda; sessionCount is how
//                                                     many lines fan into this cluster
//   ?cdaMin=0.15  ?cdaMax=0.45                       — canvas Y-axis range

import React, { useEffect, useRef } from 'react';
import { colors } from './theme';

interface PositionDef {
  label: string;
  /** Wave 20.49f — rider-authored name for this position (e.g.
   *  "Bettonville Tuck"). When present, drawLabels displays it in
   *  place of the class enum; click routing still uses `label`. */
  alias?: string;
  meanCda: number;
  variance: number; // stddev; scales per-session turbulence
  sessionCount: number;
  colorIdx: number;
  // Default (untested) position. Muted grey stream + greyed
  // "· DEFAULT" label, tappable to start a focused session for
  // that class. Flips to the class color once measured.
  ghost?: boolean;
  /** Wave 20.49n — one entry per real measurement (session) that
   *  contributed to this class. When present, seedLines() spawns
   *  one line per measurement, anchored at that measurement's own
   *  cda, with waviness driven by its own variance. Falls back to
   *  the sessionCount×centerline loop when absent. */
  measurements?: Array<{ cda: number; variance: number }>;
}

interface Params {
  avgCda: number;
  positions: PositionDef[];
  cdaMin: number;
  cdaMax: number;
  // Wave 20.49cm — px of parent chrome overlapping the top /
  // bottom of the canvas (identity strip + pill; AI card +
  // banners). mapCdaToY keeps every stream + label inside the
  // remaining band so nothing renders under the chrome. Initial
  // values from the URL; live updates via the
  // `aero-wake:insets` message (see the listener in the effect).
  topInset: number;
  bottomInset: number;
}

// Curated 5-hue palette — used only as a fallback when a
// position class isn't in POSITION_COLORS below.
const PALETTE: [number, number, number][] = [
  [106, 255, 69],   // brand green
  [90, 220, 200],   // teal
  [90, 180, 255],   // sky
  [230, 130, 220],  // cool magenta
  [255, 180, 90],   // warm amber
];

// Wave 20.49n — canonical per-class palette, copied verbatim from
// web/src/services/positionColors.ts (bare embed page can't share
// app code). Every stream for a given position paints in this
// class's color so a rider recognizes their positions the same
// way they do everywhere else (aero pane, session detail chips,
// dashboard).
const POSITION_COLORS: Record<string, [number, number, number]> = {
  drops:     [106, 255, 255], // #6affff
  hoods:     [106, 255, 69],  // #6aff45 (brand green)
  tops:      [255, 209, 102], // #ffd166
  upright:   [255, 209, 102], // #ffd166
  aero_tuck: [255, 122, 217], // #ff7ad9
  aero_bars: [255, 106, 69],  // #ff6a45
  tt:        [161, 141, 255], // #a18dff
  standing:  [255, 154, 60],  // #ff9a3c
  sprint:    [255, 90, 95],   // #ff5a5f
};

function colorForPosition(cls: string, fallbackIdx: number): [number, number, number] {
  return POSITION_COLORS[cls] ?? PALETTE[Math.abs(fallbackIdx) % PALETTE.length];
}

function readParams(): Params {
  const q = new URLSearchParams(window.location.search);
  const num = (key: string, fallback: number): number => {
    const raw = q.get(key);
    if (raw == null) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  let avgCda = Math.max(0.15, Math.min(0.5, num('avgCda', 0.25)));
  let cdaMin = num('cdaMin', 0.15);
  let cdaMax = num('cdaMax', 0.45);
  // Wave 20.49cl — `noDemo=1` means "real rider, zero tested
  // positions". The bare URL (no params) doubles as the
  // marketing/demo page and fills 5 fake measured positions; a
  // real new rider must NOT see those. With noDemo the parse
  // yields an empty list and the core-position seeding below
  // builds the rider's initial wake: all 4 defaults, greyed.
  const noDemo = q.get('noDemo') === '1';
  const topInset = Math.max(0, num('topInset', 0));
  const bottomInset = Math.max(0, num('bottomInset', 0));

  const posRaw = q.get('positions');
  let positions: PositionDef[];
  if (posRaw) {
    positions = posRaw
      .split(',')
      .map((p, i): PositionDef | null => {
        const parts = p.split(':');
        if (parts.length < 4) return null;
        const meanCda = Number(parts[1]);
        const variance = Number(parts[2]);
        const sessionCount = Number(parts[3]);
        if (!Number.isFinite(meanCda) || !Number.isFinite(variance) || !Number.isFinite(sessionCount)) {
          return null;
        }
        // Wave 20.49f — optional 5th token is the rider-authored
        // alias (URL-encoded). Class enum stays in `label` so
        // click-routing (`preselect`) still resolves; alias is
        // used only for the visible caption.
        let alias: string | undefined;
        if (parts.length >= 5 && parts[4]) {
          try {
            const decoded = decodeURIComponent(parts[4]);
            if (decoded) alias = decoded.slice(0, 32);
          } catch {
            /* ignore malformed alias — fall back to class label */
          }
        }
        // Wave 20.49n — optional 6th token: per-measurement CdAs
        // and variances joined by `;`, fields joined by `|`.
        // Example: 0.24|0.02;0.22|0.01;0.25|0.015
        // When present, each entry becomes its own stream anchored
        // at its own cda with its own waviness.
        let measurements: Array<{ cda: number; variance: number }> | undefined;
        if (parts.length >= 6 && parts[5]) {
          const raw = parts[5]
            .split(';')
            .map((m) => {
              const [c, v] = m.split('|');
              const cdaVal = Number(c);
              const varVal = Number(v);
              if (!Number.isFinite(cdaVal) || cdaVal <= 0) return null;
              const safeVar = Number.isFinite(varVal) ? Math.max(0, Math.min(0.15, varVal)) : 0;
              return {
                cda: Math.max(0.12, Math.min(0.6, cdaVal)),
                variance: safeVar,
              };
            })
            .filter((x): x is { cda: number; variance: number } => x != null);
          if (raw.length > 0) measurements = raw.slice(0, 50);
        }
        return {
          label: (parts[0] || `pos${i}`).slice(0, 20),
          alias,
          meanCda: Math.max(0.12, Math.min(0.6, meanCda)),
          variance: Math.max(0, Math.min(0.15, variance)),
          sessionCount: Math.max(1, Math.min(50, Math.round(sessionCount))),
          colorIdx: i,
          measurements,
        };
      })
      .filter((p): p is PositionDef => p != null);
    if (positions.length === 0 && !noDemo) positions = demoPositions();
  } else {
    positions = noDemo ? [] : demoPositions();
  }
  // Wave 20.49ck — Always show the 4 core positions
  // (standing / tops / hoods / drops). Untested rows are seeded
  // *relative to the rider's tested positions* using the shared
  // CD ratios so a rider whose only measurement is hoods=0.30
  // sees drops≈0.24 and standing≈0.45 — their own scale, not a
  // one-size-fits-all placeholder. Ghost styling stays (low
  // alpha, no label) so the rider can visually tell what's real.
  const CD_RATIOS_LOCAL: Record<string, number> = {
    standing: 1.35, tops: 1.10, hoods: 0.90, drops: 0.72,
    aero_tuck: 0.58, tt: 0.46,
  };
  const CORE = ['standing', 'tops', 'hoods', 'drops'];
  // Wave 20.49cm — `upright` was renamed to `tops` (Wave 20.49cj)
  // but older sessions still arrive with the legacy class. Without
  // this alias a rider whose anchor is `upright` got
  // CD_RATIOS_LOCAL[undefined] → the generic 0.36 m² placeholders
  // instead of defaults scaled from THEIR measurement, and a
  // spurious TOPS ghost could be seeded on top of a tested upright.
  const canon = (l: string): string => (l === 'upright' ? 'tops' : l);
  const knownLabels = new Set(positions.map((p) => canon(p.label.toLowerCase())));

  // Pick the anchor — tested position with the most sessions.
  let anchor: PositionDef | null = null;
  for (const p of positions) {
    if (!anchor || p.sessionCount > anchor.sessionCount) anchor = p;
  }
  const anchorRatio = anchor ? CD_RATIOS_LOCAL[canon(anchor.label.toLowerCase())] : null;

  for (const cls of CORE) {
    if (knownLabels.has(cls)) continue;
    const ratio = CD_RATIOS_LOCAL[cls];
    let seedCda: number;
    if (anchor && anchorRatio && anchorRatio > 0) {
      seedCda = (anchor.meanCda / anchorRatio) * ratio;
    } else {
      // No tested positions at all — nominal 0.36 m² × ratio.
      seedCda = 0.36 * ratio;
    }
    positions.push({
      label: cls,
      meanCda: seedCda,
      variance: 0.012,
      sessionCount: 0,
      colorIdx: -1,
      ghost: true,
    });
  }
  // Wave 20.49cl — When the client didn't send avgCda (the
  // zero-positions / noDemo case), anchor the origin bundle at
  // the mean of whatever we ended up with, so the streams fan
  // out from the middle of the default wake instead of from a
  // 0.25 default that sits below all four seeded rows.
  if (!q.has('avgCda') && positions.length > 0) {
    const mean =
      positions.reduce((s, p) => s + p.meanCda, 0) / positions.length;
    avgCda = Math.max(0.15, Math.min(0.5, mean));
  }
  // Auto-fit the Y range when the client didn't pin it. The fixed
  // 0.15–0.45 defaults clip STANDING (0.36 m² × 1.35 = 0.486) off
  // the top of the canvas — a default row you can't see isn't
  // much of a default. Pad by 0.03 so the outermost streams keep
  // some breathing room.
  if (positions.length > 0) {
    const lo = Math.min(...positions.map((p) => p.meanCda));
    const hi = Math.max(...positions.map((p) => p.meanCda));
    if (!q.has('cdaMin')) cdaMin = Math.min(cdaMin, lo - 0.03);
    if (!q.has('cdaMax')) cdaMax = Math.max(cdaMax, hi + 0.03);
  }
  return { avgCda, positions, cdaMin, cdaMax, topInset, bottomInset };
}

function demoPositions(): PositionDef[] {
  return [
    { label: 'aero_tuck', meanCda: 0.20, variance: 0.008, sessionCount: 5, colorIdx: 0 },
    { label: 'drops',     meanCda: 0.25, variance: 0.020, sessionCount: 8, colorIdx: 1 },
    { label: 'tt',        meanCda: 0.22, variance: 0.030, sessionCount: 3, colorIdx: 2 },
    { label: 'hoods',     meanCda: 0.30, variance: 0.012, sessionCount: 6, colorIdx: 3 },
    { label: 'seated',    meanCda: 0.38, variance: 0.045, sessionCount: 4, colorIdx: 4 },
  ];
}

// ---------- noise / curl ----------

function hash(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 1274126177) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  return (h & 0x7fffffff) / 0x7fffffff;
}
function fadeQ(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}
function valueNoise(x: number, y: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = fadeQ(xf);
  const v = fadeQ(yf);
  const a = hash(xi, yi, seed);
  const b = hash(xi + 1, yi, seed);
  const c = hash(xi, yi + 1, seed);
  const d = hash(xi + 1, yi + 1, seed);
  const ab = a + u * (b - a);
  const cd = c + u * (d - c);
  return ab + v * (cd - ab);
}
function curlAt(x: number, y: number, t: number, scale: number, seed: number): [number, number] {
  const eps = 1.0;
  const s = t * 0.00006;
  const sx = x * scale + s;
  const sy = y * scale;
  const nY1 = valueNoise(sx, sy + eps, seed);
  const nY0 = valueNoise(sx, sy - eps, seed);
  const nX1 = valueNoise(sx + eps, sy, seed);
  const nX0 = valueNoise(sx - eps, sy, seed);
  return [(nY1 - nY0) / (2 * eps), -(nX1 - nX0) / (2 * eps)];
}

// ---------- session-line state ----------

interface SessionLine {
  // Persistent identity
  posIdx: number;
  seed: number;
  variance: number;    // this session's own stddev (baked-in wobble)
  sessionOffset: number; // 0..1; where this session sits inside the position band
  color: [number, number, number];
  // Wave 20.47g — ghost streams for default (untested) core
  // positions. Render at low alpha; labeled + tappable since
  // Wave 20.49cl.
  ghost?: boolean;
  // Wave 20.49n — per-measurement anchor. When set, this line
  // rides at this specific CdA (not the position's aggregate
  // mean). Undefined means fall back to pos.meanCda + the
  // fan-out spread the old rendering used.
  measurementCda?: number;
  // Rendering
  x: number;           // head x
  vx: number;
  age: number;
  tailPts: Array<{ x: number; y: number }>;
  tailMax: number;
}

// Wave 20.44 — click regions painted this frame. Labels + the
// "+ Add position" pill register their bboxes here so the click
// handler can dispatch aero-wake:focus messages to the parent
// (web parent or React Native WebView).
interface ClickTarget {
  x: number;
  y: number;
  w: number;
  h: number;
  cls: string; // position class, or 'new' for the add-position pill
}

function rgba(c: [number, number, number], a: number): string {
  return `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;
}

// Feature-detects both worlds — web iframe (window.parent) and RN
// WebView (window.ReactNativeWebView). The parent listens for
// aero-wake:focus events; type is the discriminator.
function emitFocus(cls: string) {
  const payload = { type: 'aero-wake:focus', class: cls };
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, '*');
    }
  } catch { /* cross-origin — safe to ignore */ }
  const rnwv = (window as any).ReactNativeWebView;
  if (rnwv && typeof rnwv.postMessage === 'function') {
    rnwv.postMessage(JSON.stringify(payload));
  }
}

// ---------- main ----------

export default function WakeEmbed() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const paramsRef = useRef<Params>(readParams());
  // Wave 20.49cm — live chrome insets. Seeded from the URL, then
  // kept current by the parent via postMessage whenever its
  // overlay chrome (AI card, banners, identity strip) resizes.
  const insetsRef = useRef<{ top: number; bottom: number }>({
    top: paramsRef.current.topInset,
    bottom: paramsRef.current.bottomInset,
  });
  const linesRef = useRef<SessionLine[]>([]);
  const mountedAtRef = useRef<number>(0);
  // Refreshed every frame by drawLabels + drawAddPill so the canvas
  // click handler always hits current bboxes even after a resize.
  const clickTargetsRef = useRef<ClickTarget[]>([]);

  // Wave 20.45 — paint the document background dark immediately on
  // mount so the mobile WebView doesn't flash white while the SPA
  // (MUI theme + router + this component) is still initializing.
  // Mirrors what WindEmbed already does.
  useEffect(() => {
    const prevBody = document.body.style.background;
    const prevHtml = document.documentElement.style.background;
    document.body.style.background = colors.bg;
    document.documentElement.style.background = colors.bg;
    document.body.style.margin = '0';
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = prevHtml;
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;

    const mapCdaToY = (cda: number): number => {
      const { cdaMin, cdaMax } = paramsRef.current;
      // Wave 20.49cn — HIGHER CdA (more drag) sits toward the TOP,
      // lower CdA (more aero) toward the BOTTOM. Matches the
      // rider's mental model and every other surface
      // (positionDefaults ranks worst→best; the aero panel shows
      // Standing on top, Drops at the bottom). The canvas had it
      // inverted, so the wake read upside-down next to the panel.
      const t = (cda - cdaMin) / (cdaMax - cdaMin);
      const clamped = 1 - Math.max(0, Math.min(1, t));
      // Wave 20.49cm — the usable band is the canvas minus whatever
      // parent chrome overlaps each edge (+ a little breathing
      // room), never less than the 14 % margins we always kept.
      // Read live every call so a resized AI card or a banner
      // appearing shifts the streams immediately — no reseed.
      const { top: insetTop, bottom: insetBottom } = insetsRef.current;
      const marginTop = Math.max(H * 0.14, insetTop + 20);
      const marginBottom = Math.max(H * 0.14, insetBottom + 20);
      const band = Math.max(40, H - marginTop - marginBottom);
      return marginTop + clamped * band;
    };

    const seedLines = () => {
      const lines: SessionLine[] = [];
      const params = paramsRef.current;
      params.positions.forEach((pos, posIdx) => {
        // Wave 20.49n — every position streams in ITS own class
        // color (matches the aero pane / dashboard chips), not
        // an index-into-PALETTE. Ghosts stay muted gray so they
        // read as aspirational.
        const posColor: [number, number, number] = pos.ghost
          ? [140, 148, 165]
          : colorForPosition(pos.label, posIdx);
        // Wave 20.49n — when the URL carried per-measurement data,
        // spawn one line per real measurement anchored at its own
        // cda + waviness. Otherwise fall back to the aggregate
        // sessionCount×centerline loop (legacy demo data too).
        if (pos.measurements && pos.measurements.length > 0) {
          const N = pos.measurements.length;
          pos.measurements.forEach((m, i) => {
            const t = N === 1 ? 0 : (i / (N - 1)) - 0.5;
            const jitter = (hash(posIdx * 91, i * 37, 3) - 0.5) * m.variance * 8;
            lines.push({
              posIdx,
              seed: 1000 + posIdx * 100 + i,
              variance: m.variance,
              sessionOffset: t + jitter / 30,
              color: posColor,
              ghost: pos.ghost,
              measurementCda: m.cda,
              x: -30 - Math.random() * W * 0.4,
              vx: 0.8 + Math.random() * 0.6,
              age: 0,
              tailPts: [],
              tailMax: 90,
            });
          });
          return;
        }
        // Wave 20.49cm — default (ghost) positions are seeded with
        // sessionCount 0, so the session loop below spawned NO
        // stream for them: a DEFAULT label floated with nothing
        // pointing at it. Every default now gets exactly one grey
        // stream riding its centerline, so the row reads as a real
        // place in the wake the rider hasn't filled in yet.
        if (pos.ghost) {
          lines.push({
            posIdx,
            seed: 1000 + posIdx * 100,
            variance: pos.variance,
            sessionOffset: 0,
            color: posColor,
            ghost: true,
            x: -30 - Math.random() * W * 0.4,
            vx: 0.8 + Math.random() * 0.6,
            age: 0,
            tailPts: [],
            tailMax: 90,
          });
          return;
        }
        for (let i = 0; i < pos.sessionCount; i++) {
          // Offset sessions symmetrically around the position centerline.
          const t = pos.sessionCount === 1
            ? 0
            : (i / (pos.sessionCount - 1)) - 0.5;
          // Per-session variance perturbs the seat position slightly
          // so lines don't all overlap.
          const jitter = (hash(posIdx * 91, i * 37, 3) - 0.5) * pos.variance * 8;
          const line: SessionLine = {
            posIdx,
            seed: 1000 + posIdx * 100 + i,
            variance: pos.variance,
            sessionOffset: t + jitter / 30,
            color: posColor,
            ghost: pos.ghost,
            x: -30 - Math.random() * W * 0.4,
            vx: 0.8 + Math.random() * 0.6,
            age: 0,
            tailPts: [],
            tailMax: 90,
          };
          lines.push(line);
        }
      });
      linesRef.current = lines;
    };

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedLines();
    };

    // Per-line trajectory: y at a given x is a blend of the origin
    // bundle (all lines pinned to avgCda at x=0) and the position's
    // Y band (spread by sessionOffset × amplitude), + curl noise
    // scaled by the session's own variance.
    const lineY = (line: SessionLine, x: number, tNow: number): number => {
      const params = paramsRef.current;
      const pos = params.positions[line.posIdx];
      const originY = mapCdaToY(params.avgCda);
      // Wave 20.49n — each line rides at its own measurement's
      // CdA when present; otherwise the whole position bundle
      // sits on the aggregate mean like before.
      const anchorCda = line.measurementCda ?? pos.meanCda;
      const bandY = mapCdaToY(anchorCda);
      // Amplitude of the fan-out spread. When lines already sit
      // at their own measurement CdA, the spread is much smaller
      // (just enough to keep them from perfectly overlapping when
      // two sessions happen to land at the same value).
      const amplitude = line.measurementCda != null
        ? clamp(2, 20, line.variance * 200)
        : clamp(6, 90, pos.variance * 900);
      const seatY = bandY + line.sessionOffset * amplitude * 2;
      // Blend origin → seat over the first ~35 % of the canvas.
      const xNorm = Math.max(0, Math.min(1, x / W));
      const fan = smoothstep(0, 0.35, xNorm);
      const baseY = originY + (seatY - originY) * fan;
      // Per-line curl turbulence — noise scale climbs with variance
      // so high-variance sessions writhe visibly.
      const noiseScale = 0.004 + line.variance * 0.05;
      const [, ny] = curlAt(x, seatY, tNow, noiseScale, line.seed);
      // Suppress noise near the origin so the bundle stays tight.
      const noiseGain = fan * clamp(2, 32, line.variance * 400);
      return baseY + ny * noiseGain;
    };

    const drawLabels = () => {
      const params = paramsRef.current;
      const nextTargets: ClickTarget[] = [];
      ctx.save();
      ctx.font = '600 11px "JetBrains Mono", ui-monospace, monospace';
      ctx.textBaseline = 'middle';
      // Left: avg CdA origin tag (not clickable — it's an aggregate)
      ctx.textAlign = 'left';
      ctx.fillStyle = 'rgba(200,220,220,0.55)';
      const originY = mapCdaToY(params.avgCda);
      ctx.fillText(`AVG CdA · ${params.avgCda.toFixed(3)}`, 14, originY);
      // Right: per-position tags. Each becomes a click target — tap
      // to jump into a focused refine session for that class.
      ctx.textAlign = 'right';
      for (const pos of params.positions) {
        // Wave 20.49cl — Default (ghost) positions now get a label
        // too. The rider's wake starts as the 4 core positions at
        // default CdA values; a default row must show its NAME so
        // the rider knows what's there to test — it was invisible
        // before ("ghost streams are unlabeled"), which read as
        // "only tested positions exist". Greyed out to match the
        // muted stream color, tagged DEFAULT, and tappable so a
        // default row is the shortcut into a focused session for
        // that position (how the rider defines their wake over
        // time). Once measured, the row flips to its class color.
        const y = mapCdaToY(pos.meanCda);
        const color: [number, number, number] = pos.ghost
          ? [140, 148, 165]
          : PALETTE[pos.colorIdx];
        // Wave 20.49f — prefer the rider-authored alias when
        // present, else fall back to the class enum label.
        const caption = (pos.alias || pos.label).toUpperCase();
        const text = pos.ghost
          ? `${caption} · ${pos.meanCda.toFixed(3)} · DEFAULT`
          : `${caption} · ${pos.meanCda.toFixed(3)}`;
        // Underline hint so it reads as tappable at a glance.
        const metrics = ctx.measureText(text);
        const textW = Math.min(metrics.width, W * 0.5);
        const textH = 14;
        const boxX = W - 14 - textW - 8;
        const boxY = y - textH / 2 - 4;
        // Subtle chip background so the tap target reads as one unit.
        // Ghost rows sit at lower alpha across the board so they
        // recede behind real measurements.
        const bgA = pos.ghost ? 0.05 : 0.09;
        const strokeA = pos.ghost ? 0.22 : 0.35;
        const textA = pos.ghost ? 0.55 : 0.9;
        ctx.fillStyle = rgba(color, bgA);
        ctx.fillRect(boxX, boxY, textW + 16, textH + 8);
        ctx.strokeStyle = rgba(color, strokeA);
        ctx.lineWidth = 1;
        ctx.strokeRect(boxX + 0.5, boxY + 0.5, textW + 15, textH + 7);
        // Foreground label text.
        ctx.fillStyle = rgba(color, textA);
        ctx.fillText(text, W - 14 - 4, y);
        // Register the click bbox against the real class string
        // (not the display label) so the parent can preselect the
        // tile on /session/focus.
        nextTargets.push({
          x: boxX,
          y: boxY,
          w: textW + 16,
          h: textH + 8,
          cls: pos.label,
        });
      }
      ctx.restore();
      clickTargetsRef.current = nextTargets;
    };

    const drawAddPill = () => {
      // Rendered AFTER labels so its click target is registered last
      // and the pill sits above any overlap.
      const pillW = 130;
      const pillH = 28;
      const pillX = W - 14 - pillW;
      const pillY = 14;
      ctx.save();
      ctx.fillStyle = 'rgba(10, 14, 26, 0.75)';
      ctx.strokeStyle = 'rgba(106, 255, 69, 0.55)';
      ctx.lineWidth = 1;
      roundRect(ctx, pillX, pillY, pillW, pillH, 999);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = 'rgba(106, 255, 69, 0.95)';
      ctx.font = '700 10.5px "JetBrains Mono", ui-monospace, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('+ ADD POSITION', pillX + pillW / 2, pillY + pillH / 2);
      ctx.restore();
      clickTargetsRef.current.push({
        x: pillX,
        y: pillY,
        w: pillW,
        h: pillH,
        cls: 'new',
      });
    };

    const stepAndDraw = (tNow: number) => {
      // Trail dim.
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(10,14,26,0.10)';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      for (const line of linesRef.current) {
        // Advance head.
        line.x += line.vx;
        line.age += 1;
        if (line.x > W + 40) {
          // Respawn — start behind left edge with a fresh phase.
          line.x = -30 - Math.random() * W * 0.2;
          line.tailPts.length = 0;
          continue;
        }
        // Sample current head y.
        const yHead = lineY(line, line.x, tNow);
        line.tailPts.push({ x: line.x, y: yHead });
        if (line.tailPts.length > line.tailMax) line.tailPts.shift();
        // Draw tail as a poly-line.
        if (line.tailPts.length >= 2) {
          // Wave 20.49cm — ghosts render with NORMAL compositing.
          // The canvas paints in `lighter` (additive) mode so real
          // streams bloom bright where strokes stack frame over
          // frame — but that same stacking pushed a grey ghost
          // toward white, so defaults looked as lit-up as measured
          // positions. Ghosts now composite source-over (no
          // accumulation) at a fixed low alpha, thinner, and dashed,
          // so they are unmistakably "not measured yet".
          ctx.globalCompositeOperation = line.ghost ? 'source-over' : 'lighter';
          ctx.setLineDash(line.ghost ? [6, 7] : []);
          const tailAlpha = line.ghost ? 0.32 : 0.6;
          const headAlpha = line.ghost ? 0.42 : 0.9;
          ctx.strokeStyle = rgba(line.color, tailAlpha);
          ctx.lineWidth = line.ghost ? 1.0 : 1.3;
          ctx.beginPath();
          const first = line.tailPts[0];
          ctx.moveTo(first.x, first.y);
          for (let i = 1; i < line.tailPts.length; i++) {
            const p = line.tailPts[i];
            ctx.lineTo(p.x, p.y);
          }
          ctx.stroke();
          const head = line.tailPts[line.tailPts.length - 1];
          ctx.fillStyle = rgba(line.color, headAlpha);
          ctx.beginPath();
          ctx.arc(head.x, head.y, line.ghost ? 1.2 : 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.globalCompositeOperation = 'source-over';
      // Labels on top so streams don't overwrite them.
      drawLabels();
      // Wave 20.47g — canvas-painted "+ ADD POSITION" pill removed.
      // On mobile it sat under the iPhone notch (canvas is full-
      // viewport inside the WebView with no safe-area padding), and
      // both mobile Wake + web Wake already render a native
      // button below the AppHeader. One button, in the right place.
      // Fade-in veil.
      const veilAlpha = Math.max(0, 1 - (tNow - mountedAtRef.current) / 900);
      if (veilAlpha > 0) {
        ctx.fillStyle = `rgba(10,14,26,${veilAlpha})`;
        ctx.fillRect(0, 0, W, H);
      }
    };

    const tick = () => {
      stepAndDraw(performance.now());
      rafRef.current = requestAnimationFrame(tick);
    };
    const start = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
    const onVis = () => {
      if (document.hidden) stop();
      else start();
    };

    const onClick = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const cx = ev.clientX - rect.left;
      const cy = ev.clientY - rect.top;
      // Iterate in reverse so the top-most (last-drawn) target wins
      // in the overlap zone — the add-position pill.
      const targets = clickTargetsRef.current;
      for (let i = targets.length - 1; i >= 0; i--) {
        const t = targets[i];
        if (cx >= t.x && cx <= t.x + t.w && cy >= t.y && cy <= t.y + t.h) {
          emitFocus(t.cls);
          return;
        }
      }
    };
    // Passive cursor hint — canvas is default 'default', but tapping
    // sensitive regions gives users a pointer.
    const onMove = (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const cx = ev.clientX - rect.left;
      const cy = ev.clientY - rect.top;
      const targets = clickTargetsRef.current;
      let over = false;
      for (const t of targets) {
        if (cx >= t.x && cx <= t.x + t.w && cy >= t.y && cy <= t.y + t.h) {
          over = true;
          break;
        }
      }
      canvas.style.cursor = over ? 'pointer' : 'default';
    };

    window.addEventListener('resize', resize);
    // Wave 20.49cm — parent posts its overlay chrome geometry so the
    // canvas lays out AROUND the AI card / banners / identity strip
    // instead of under them. Web parent uses iframe postMessage
    // (ev.data is an object); the RN WebView parent injects a
    // synthetic MessageEvent whose data is a JSON string — accept
    // both.
    const onInsetsMessage = (ev: MessageEvent) => {
      let d: any = ev.data;
      if (typeof d === 'string') {
        try { d = JSON.parse(d); } catch { return; }
      }
      if (!d || typeof d !== 'object' || d.type !== 'aero-wake:insets') return;
      const top = Number(d.top);
      const bottom = Number(d.bottom);
      insetsRef.current = {
        top: Number.isFinite(top) ? Math.max(0, top) : insetsRef.current.top,
        bottom: Number.isFinite(bottom) ? Math.max(0, bottom) : insetsRef.current.bottom,
      };
    };
    window.addEventListener('message', onInsetsMessage);
    document.addEventListener('visibilitychange', onVis);
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('mousemove', onMove);
    mountedAtRef.current = performance.now();
    resize();
    ctx.fillStyle = colors.bg;
    ctx.fillRect(0, 0, W, H);
    start();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('message', onInsetsMessage);
      document.removeEventListener('visibilitychange', onVis);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('mousemove', onMove);
      stop();
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        backgroundColor: colors.bg,
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
}

// ---------- utils ----------

function clamp(min: number, max: number, v: number): number {
  return Math.max(min, Math.min(max, v));
}
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp(0, 1, (x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
